"use client";

import { GET_USER_QUERY, LOGIN_MUTATION, SEND_EMAIL_OTP_MUTATION, SEND_OTP_MUTATION, VERIFY_EMAIL_OTP_MUTATION, VERIFY_OTP_MUTATION, type AuthParams, type LoginResponse, type SendOtpResponse, type User, type VerifyOtpResponse } from "@/gql/auth";
import { useSelectedGroupSync } from "@/hooks/use-selected-group-sync";
import { useBuyerStore, useSellerStore } from "@/providers/global";
import { trackLogin } from "@/utils/analytics/track-login";
import { logger } from "@/utils/logger";
import { cacheUser, clearAllCache, getCachedUser, type CachedUser } from "@/utils/user-preferences";
import { useApolloClient, useMutation } from "@apollo/client";
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { flushSync } from "react-dom";

// Interface for user group structure
interface UserGroup {
  id: string;
  relationship: string;
}

// Constants
const AUTH_TOKEN_KEY = "settlin_auth_token";
const AUTH_USER_KEY = "settlin_auth_user";

// Storage utilities with IndexedDB for user data
const storage = {
  getToken: (): string | null => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(AUTH_TOKEN_KEY);
  },
  getUser: async (): Promise<User | null> => {
    if (typeof window === "undefined") return null;
    try {
      // Try to get from IndexedDB first
      const cachedUser = await getCachedUser();
      if (cachedUser) {
        return cachedUser as User;
      }

      // Fall back to localStorage for migration
      const userStr = localStorage.getItem(AUTH_USER_KEY);
      if (userStr) {
        const user = JSON.parse(userStr);
        // Cache in IndexedDB for future use
        await cacheUser(user);
        // Remove from localStorage
        localStorage.removeItem(AUTH_USER_KEY);
        return user;
      }
      return null;
    } catch (error) {
      logger.warn("Failed to get user from IndexedDB, falling back to localStorage:", error);
      const userStr = localStorage.getItem(AUTH_USER_KEY);
      try {
        return userStr ? JSON.parse(userStr) : null;
      } catch {
        return null;
      }
    }
  },
  setAuth: async (token: string, user: User): Promise<void> => {
    if (typeof window === "undefined") return;
    try {
      // Store token in localStorage (smaller, needs to be sync)
      localStorage.setItem(AUTH_TOKEN_KEY, token);

      // Cache user in IndexedDB
      await cacheUser(user as CachedUser);

      // Remove from localStorage if it exists (migration)
      localStorage.removeItem(AUTH_USER_KEY);
    } catch (error) {
      logger.warn("Failed to cache user in IndexedDB, falling back to localStorage:", error);
      try {
        localStorage.setItem(AUTH_TOKEN_KEY, token);
        localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
      } catch (fallbackError) {
        throw new Error(`Storage operation failed completely: ${fallbackError}`);
      }
    }
  },
  clearAuth: async (): Promise<void> => {
    if (typeof window === "undefined") return;
    try {
      // Clear localStorage
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_KEY);

      // Clear all IndexedDB cache
      await clearAllCache();
    } catch (error) {
      logger.warn("Failed to clear cache from IndexedDB:", error);
      // Still clear localStorage
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(AUTH_USER_KEY);
    }
  }
};

// Auth context types
interface AuthContextValue {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  isHydrated: boolean;
  setAuthData: (token: string, user: User) => Promise<void>;
  clearAuthData: () => Promise<void>;
}

// Create the auth context
const AuthContext = createContext<AuthContextValue | null>(null);

// Component to handle seller data synchronization

// Auth provider component
export function AuthProvider({
  children
}: {
  children: ReactNode;
}) {
  const buyerStore = useBuyerStore();
  const sellerStore = useSellerStore();
  const [authData, setAuthData] = useState<{
    isAuthenticated: boolean;
    user: User | null;
    token: string | null;
    isHydrated: boolean;
  }>({
    isAuthenticated: false,
    user: null,
    token: null,
    isHydrated: false
  });

  // Initialize selectedGroup sync between IndexedDB and Zustand store
  useSelectedGroupSync();

  // Initialize from storage after hydration
  useEffect(() => {
    async function initializeAuth() {
      try {
        const storedToken = storage.getToken();
        const storedUser = await storage.getUser();
        const groups = storedUser?.groups;
        const buyerId = groups?.find((g: UserGroup) => g.relationship === "buyer")?.id || null;

        // Sync buyerId on initial load
        if (buyerId) {
          buyerStore.getState().setBuyerId(buyerId);
        } else {
          buyerStore.getState().setBuyerId(null);
        }

        // Seller ID sync removed as it's now handled by user-preferences store

        setAuthData({
          token: storedToken,
          user: storedUser,
          isAuthenticated: !!(storedToken && storedUser),
          isHydrated: true
        });

        // Track session resume if user was already authenticated
        if (storedToken && storedUser) {
          try {
            await trackLogin({
              userData: {
                id: storedUser.id,
                name: storedUser.name,
                profile: storedUser.profile,
                emails: storedUser.emails,
                phones: storedUser.phones,
                groups: storedUser.groups,
                internal: Boolean(storedUser.internal)
              },
              loginType: "session_resume"
            });
          } catch (trackingError) {
            // Don't fail initialization if tracking fails
            logger.debug("Session resume tracking failed (non-critical):", trackingError);
          }
        }
      } catch (error) {
        // Enhanced error logging with more context
        if (error instanceof Error && error.message.includes("Access is denied for this document")) {
          // Expected in private browsing mode
          logger.debug("LocalStorage access denied (expected in private mode):", error);
        } else {
          const errorDetails = {
            message: error instanceof Error ? error.message : "Unknown error",
            stack: error instanceof Error ? error.stack : undefined,
            name: error instanceof Error ? error.name : "UnknownError",
            userAgent: typeof window !== "undefined" ? window.navigator?.userAgent : "server",
            indexedDBSupported: typeof window !== "undefined" && "indexedDB" in window,
            localStorageSupported: typeof window !== "undefined" && "localStorage" in window,
            timestamp: new Date().toISOString()
          };
          logger.error("Failed to initialize auth from storage:", errorDetails);
        }

        // Set auth data to unauthenticated state but still mark as hydrated
        setAuthData({
          token: null,
          user: null,
          isAuthenticated: false,
          isHydrated: true
        });

        // Clear any potentially corrupted data
        try {
          if (typeof window !== "undefined") {
            localStorage.removeItem(AUTH_TOKEN_KEY);
            localStorage.removeItem(AUTH_USER_KEY);
          }
        } catch (cleanupError) {
          logger.warn("Failed to cleanup corrupted auth data:", cleanupError);
        }
      }
    }
    initializeAuth();
  }, [buyerStore, sellerStore]);
  const setAuthDataCallback = useCallback(async (newToken: string, newUser: User) => {
    try {
      await storage.setAuth(newToken, newUser);

      // Use flushSync to ensure state updates are applied synchronously
      // This prevents race conditions where components try to access the updated user/relation
      // before React has finished rendering
      flushSync(() => {
        setAuthData({
          token: newToken,
          user: newUser,
          isAuthenticated: true,
          isHydrated: true
        });
      });
      const groups_0 = newUser?.groups;
      const buyerId_0 = groups_0?.find((g_0: UserGroup) => g_0.relationship === "buyer")?.id || null;
      // const sellerId = groups?.find((g: UserGroup) => g.relationship === "seller")?.id || null;

      try {
        buyerStore.getState().setBuyerId(buyerId_0);
      } catch (storeError) {
        throw storeError;
      }
    } catch (error_0) {
      logger.error("Failed to set auth data:", error_0);
    }
  }, [buyerStore]);
  const clearAuthData = useCallback(async () => {
    try {
      await storage.clearAuth();
      setAuthData({
        token: null,
        user: null,
        isAuthenticated: false,
        isHydrated: true
      });

      // Clear buyerId from global store
      buyerStore.getState().setBuyerId(null);

      // Clear sellerId and rawUnits from global store
      // Seller ID is no longer in global store

      sellerStore.getState().setProperties([]);
    } catch (error_1) {
      logger.error("Failed to clear auth data:", error_1);
    }
  }, [buyerStore, sellerStore]);
  const contextValue: AuthContextValue = {
    ...authData,
    setAuthData: setAuthDataCallback,
    clearAuthData
  };

  // Get seller ID for SellerDataSync component
  // const sellerId = authData.user?.groups?.find((g: UserGroup) => g.relationship === "seller")?.id || null;

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

// Hook to use auth context
function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}

// Hook for OTP operations
function useOtpOperations() {
  const [sendOtpMutation, {
    loading: sendOtpLoading
  }] = useMutation<{
    auth: SendOtpResponse;
  }, {
    params: AuthParams;
  }>(SEND_OTP_MUTATION);
  const [sendEmailOtpMutation, {
    loading: sendEmailOtpLoading
  }] = useMutation<{
    auth: SendOtpResponse;
  }, {
    params: AuthParams;
  }>(SEND_EMAIL_OTP_MUTATION);
  const [verifyOtpMutation, {
    loading: verifyOtpLoading
  }] = useMutation<{
    auth: VerifyOtpResponse;
  }, {
    params: AuthParams;
  }>(VERIFY_OTP_MUTATION);
  const [verifyOtpMailMutation, {
    loading: verifyOtpMailLoading
  }] = useMutation<{
    auth: VerifyOtpResponse;
  }, {
    params: AuthParams;
  }>(VERIFY_EMAIL_OTP_MUTATION);
  const sendOtp = useCallback(async (phone: string, resend = false, method: "sms" | "whatsapp" = "whatsapp"): Promise<boolean> => {
    try {
      const {
        data
      } = await sendOtpMutation({
        variables: {
          params: {
            sendOtp: {
              phone,
              resend,
              purpose: "__login__",
              sendHashCodes: true,
              whatsapp: method === "whatsapp"
            }
          }
        }
      });
      const success = data?.auth?.sendOtp ?? false;
      return success;
    } catch (error) {
      logger.debug("Failed to send OTP - user can retry:", error);
      return false;
    }
  }, [sendOtpMutation]);
  const sendOtpMail = useCallback(async (email: string, name: string, resend_0 = false): Promise<boolean> => {
    try {
      const {
        data: data_0
      } = await sendEmailOtpMutation({
        variables: {
          params: {
            sendOtpMail: {
              email,
              name,
              resend: resend_0
            }
          }
        }
      });
      const success_0 = data_0?.auth?.sendOtpMail ?? false;
      return success_0;
    } catch (error_0) {
      logger.debug("Failed to send OTP - user can retry:", error_0);
      return false;
    }
  }, [sendEmailOtpMutation]);
  const verifyOtp = useCallback(async (phone_0: string, otp: string, purpose = "__login__"): Promise<boolean> => {
    try {
      const {
        data: data_1
      } = await verifyOtpMutation({
        variables: {
          params: {
            verifyOtp: {
              phone: phone_0,
              otp,
              purpose
            }
          }
        }
      });
      const success_1 = data_1?.auth?.verifyOtp ?? false;
      return success_1;
    } catch (error_1) {
      logger.debug("Failed to verify OTP - user can retry:", error_1);
      return false;
    }
  }, [verifyOtpMutation]);
  const verifyOtpMail = useCallback(async (email_0: string, otp_0: string, url = "webApp"): Promise<boolean> => {
    try {
      const {
        data: data_2
      } = await verifyOtpMailMutation({
        variables: {
          params: {
            verifyOtpMail: {
              email: email_0,
              otp: otp_0,
              url
            }
          }
        }
      });
      const success_2 = data_2?.auth?.verifyOtpMail ?? false;
      return success_2;
    } catch (error_2) {
      logger.debug("Failed to verify OTP - user can retry:", error_2);
      return false;
    }
  }, [verifyOtpMailMutation]);
  return {
    sendOtp,
    sendOtpMail,
    verifyOtp,
    verifyOtpMail,
    isLoading: sendOtpLoading || verifyOtpLoading || sendEmailOtpLoading || verifyOtpMailLoading
  };
}

// Hook for login operations
function useLoginOperations() {
  const [loginMutation, {
    loading: loginLoading
  }] = useMutation<{
    auth: {
      login: LoginResponse;
    };
  }, {
    params: AuthParams;
  }>(LOGIN_MUTATION);
  const login = useCallback(async (phone: string, otp: string): Promise<LoginResponse | null> => {
    try {
      const {
        data
      } = await loginMutation({
        variables: {
          params: {
            login: {
              phone,
              otp
            }
          }
        }
      });
      const loginData = data?.auth?.login;
      return loginData || null;
    } catch (error) {
      logger.error("Login failed:", error);
      return null;
    }
  }, [loginMutation]);
  const logout = useCallback(async (): Promise<void> => {
    try {
      // For now, just clear local storage
      // The server logout mutation might not be properly configured
      // await logoutMutation({
      // 	variables: {
      // 		params: {},
      // 	},
      // });
    } catch (error_0) {
      logger.error("Logout failed:", error_0);
    }
  }, []);
  return {
    login,
    logout,
    isLoading: loginLoading
  };
}

// Main auth hook that combines everything
export function useAuth() {
  const authState = useAuthContext();
  const otpOps = useOtpOperations();
  const {
    login,
    logout,
    isLoading: loginLoading
  } = useLoginOperations();
  const [error, setError] = useState<string | null>(null);
  const apolloClient = useApolloClient();

  // Combined login function that handles both login and state update
  const handleLogin = useCallback(async (phone: string, otp: string): Promise<LoginResponse | null> => {
    try {
      setError(null);
      const loginData = await login(phone, otp);
      if (loginData?.tokenData?.token && loginData?.user) {
        await authState.setAuthData(loginData.tokenData.token, loginData.user);

        // Track successful phone OTP login
        try {
          await trackLogin({
            userData: {
              id: loginData.user.id,
              name: loginData.user.name,
              profile: loginData.user.profile,
              emails: loginData.user.emails,
              phones: loginData.user.phones,
              groups: loginData.user.groups,
              internal: Boolean(loginData.user.internal)
            },
            loginType: "phone_otp"
          });
        } catch (trackingError) {
          // Don't fail login if tracking fails
          logger.debug("Login tracking failed (non-critical):", trackingError);
        }
        return loginData;
      } else {
        const errorMsg = "Invalid login response";
        setError(errorMsg);
        return null;
      }
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : "Login failed";
      logger.error("Login error:", errorMsg);
      setError(errorMsg);
      return null;
    }
  }, [login, authState]);

  // Combined logout function
  const handleLogout = useCallback(async (): Promise<void> => {
    try {
      await logout();
    } catch (error) {
      logger.error("Logout error:", error);
    } finally {
      await authState.clearAuthData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logout, authState.clearAuthData]);
  const clearError = useCallback(() => {
    setError(null);
  }, []);
  const refetchUser = useCallback(async (user: User) => {
    const token = storage.getToken();
    if (token) {
      await storage.setAuth(token, user);
      // Use flushSync wrapped setAuthData to ensure synchronous relation updates
      // This is critical when relation changes (buyer <-> seller switch) need to be
      // immediately visible to components that depend on user.relation
      await authState.setAuthData(token, user);
    }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [authState.setAuthData]);
  const isValidToken = (str?: string) => str && str?.length > 16 && str.length < 50 && /^[a-zA-Z0-9+/=_-]+$/.test((str || "").trim());

  /**
   * Fetches fresh user data from the server and updates the cache.
   * Useful when cached user data becomes stale (e.g., group was deleted).
   * Returns the fresh user data or null if fetch fails.
   */
  const fetchFreshUserData = useCallback(async (authToken?: string): Promise<User | null> => {
    if (authToken && isValidToken(authToken) && !storage.getToken()) {
      localStorage.setItem(AUTH_TOKEN_KEY, authToken);
    }
    const token = storage.getToken();
    try {
      const {
        data
      } = await apolloClient.query<{
        auth: {
          user: User;
        };
      }>({
        query: GET_USER_QUERY,
        variables: {
          params: {
            user: {}
          }
        },
        fetchPolicy: "network-only"
      });
      const freshUser = data?.auth?.user;
      if (freshUser) {
        if (token) {
          await storage.setAuth(token, freshUser);
          await authState.setAuthData(token, freshUser);
          logger.info("Successfully refreshed user data from server");
        }
        return freshUser;
      }
      return null;
    } catch (err) {
      logger.error("Failed to fetch fresh user data:", err);
      return null;
    }
  }, [apolloClient, authState]);

  // Don't show authenticated state until hydrated
  const isAuthenticated = authState.isHydrated ? authState.isAuthenticated : false;
  const user = authState.isHydrated ? authState.user : null;

  /**
   * isLoading: Tracks initial auth hydration (checking localStorage/IndexedDB for existing session)
   * - Used by protected routes to show loading state during app initialization
   * - Only true during first mount, becomes false once hydration completes
   *
   * isLoginStateLoading: Tracks active login/OTP operations
   * - Used by login forms to show loading spinners during user login actions
   * - True while sendOtp, verifyOtp, or login mutations are in progress
   */
  const isLoading = !authState.isHydrated;
  const isLoginStateLoading = otpOps.isLoading || loginLoading;
  return {
    // State
    isAuthenticated,
    user,
    token: authState.token,
    isLoading,
    isLoginStateLoading,
    error,
    // Actions
    sendOtp: otpOps.sendOtp,
    sendOtpMail: otpOps.sendOtpMail,
    login: handleLogin,
    verifyOtp: otpOps.verifyOtp,
    verifyOtpMail: otpOps.verifyOtpMail,
    logout: handleLogout,
    clearError,
    refetchUser,
    fetchFreshUserData
  };
}